import React from "react";
import { useNavigate } from "react-router-dom";
import "./BackButton.css";

/**
 * BackButton – Premium Reusable Navigation Component
 *
 * Props:
 *  @param {string}   [label="Back"]    – Button text (pass "" or null to hide label)
 *  @param {string}   [to]              – Explicit route to navigate to (overrides navigate(-1))
 *  @param {object}   [state]           – Router state to pass when using `to`
 *  @param {function} [onClick]         – Custom click handler (overrides default navigation)
 *  @param {string}   [size="md"]       – "sm" | "md" | "lg"
 *  @param {string}   [variant="default"] – "default" | "ghost" | "filled" | "pill"
 *  @param {string}   [className]       – Extra CSS classes
 *  @param {object}   [style]           – Inline styles
 *  @param {string}   [title]           – Tooltip / aria-label
 */
const BackButton = ({
    label = "Back",
    to,
    state,
    onClick,
    size = "md",
    variant = "default",
    className = "",
    style,
    title = "Go back",
}) => {
    const navigate = useNavigate();

    const handleClick = (e) => {
        e.preventDefault();
        if (onClick) {
            onClick(e);
            return;
        }
        if (to) {
            navigate(to, { state });
        } else {
            navigate(-1);
        }
    };

    const sizeClass = size !== "md" ? `back-btn--${size}` : "";
    const variantClass = variant !== "default" ? `back-btn--${variant}` : "";

    return (
        <button
            type="button"
            className={`back-btn ${sizeClass} ${variantClass} ${className}`.trim()}
            style={style}
            onClick={handleClick}
            title={title}
            aria-label={title}
        >
            <span className="back-btn__icon" aria-hidden="true">
                {/* Arrow-left icon (pure SVG – no extra dependencies needed) */}
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="M19 12H5" />
                    <path d="M12 19l-7-7 7-7" />
                </svg>
            </span>
            {label && <span className="back-btn__label">{label}</span>}
        </button>
    );
};

export default BackButton;
